// What changed in the drawing: the ONE answer every cache keys on.
//
// The drawing is edited in place from hundreds of places, so a change is
// DETECTED, never declared: `touch()` says something may have changed (an edit,
// a drag step, a repaint request), and the next read compares every object
// against the copy kept at the last look. What differs gets a new version, and
// the drawing's revision moves only when something did. A hover that repaints
// moves nothing; a drag step moves the dragged object and the revision; an undo
// moves only what it put back.
//
// - An entry is one object of a top-level array whose items all carry a unique
//   string `id` (a room, a wall, a sheet), or else a whole top-level field
//   (`units`, `layers`, `section3d`). The order of an array's ids is an entry
//   too (the stacking order lives there).
// - `background` (the imported plan, never edited in place: undo.md) is compared
//   by identity, and so is every string, so a held PDF costs a pointer compare.
// - A version is the revision the entry last changed at, so (key, version)
//   never repeats, even across a delete and an undo.
// DOM-free (test/changeTracker.test.mjs).

export interface ChangeTracker {
  /** Something may have changed since the last look. */
  touch(): void;
  /** The drawing's revision: moves when a look finds any entry changed, added
   *  or gone (a look happens here when touched, or when handed another drawing). */
  revision(drawing: object): number;
  /** The revision leaving out some top-level fields (the 3D view ignores
   *  `section3d`, which only clips it). */
  revisionExcept(drawing: object, fields: readonly string[]): number;
  /** An object's version: the revision it last changed at (-1: not in the drawing). */
  versionOf(drawing: object, field: string, id: string): number;
  /** A top-level field's version: the revision anything in it last changed,
   *  came or went at (-1: never seen). */
  fieldVersion(drawing: object, field: string): number;
}

import {
  copyData,
  sameData,
} from './plainData';

type Data = Record<string, unknown>;

const SEP = '\u0000';
const ORDER = `${SEP}#order`;

/** The items of an array of objects with unique string ids, else null. */
function idItems(v: unknown): Data[] | null {
  if (!Array.isArray(v) || !v.length) return null;
  const ids = new Set<string>();
  for (const it of v) {
    const id = it && typeof it === 'object' ? (it as Data).id : undefined;
    if (typeof id !== 'string' || ids.has(id)) return null;
    ids.add(id);
  }
  return v as Data[];
}

export function createChangeTracker(): ChangeTracker {
  let dirty = true;
  let rev = 0;
  let seen: object | null = null;
  const kept = new Map<string, { copy: unknown; version: number }>();
  const fieldVersion = new Map<string, number>();

  const look = (drawing: object): void => {
    if (!dirty && drawing === seen) return;
    dirty = false;
    seen = drawing;
    const next = rev + 1;
    let changed = false;
    const present = new Set<string>();
    const check = (key: string, field: string, v: unknown, byIdentity: boolean): void => {
      present.add(key);
      const k = kept.get(key);
      if (k && (byIdentity ? k.copy === v : sameData(v, k.copy))) return;
      kept.set(key, { copy: byIdentity ? v : copyData(v), version: next });
      fieldVersion.set(field, next);
      changed = true;
    };
    const d = drawing as Data;
    for (const field of Object.keys(d)) {
      const v = d[field];
      if (v === undefined) continue;
      const items = idItems(v);
      if (!items) { check(field, field, v, field === 'background'); continue; }
      for (const it of items) check(field + SEP + (it.id as string), field, it, false);
      check(field + ORDER, field, items.map((it) => it.id), false);
    }
    for (const key of kept.keys()) {
      if (present.has(key)) continue;
      kept.delete(key);
      const i = key.indexOf(SEP);
      fieldVersion.set(i < 0 ? key : key.slice(0, i), next);
      changed = true;
    }
    if (changed) rev = next;
  };

  return {
    touch() { dirty = true; },
    revision(drawing) { look(drawing); return rev; },
    revisionExcept(drawing, fields) {
      look(drawing);
      let r = 0;
      for (const [f, v] of fieldVersion) if (v > r && !fields.includes(f)) r = v;
      return r;
    },
    versionOf(drawing, field, id) {
      look(drawing);
      return kept.get(field + SEP + id)?.version ?? -1;
    },
    fieldVersion(drawing, field) {
      look(drawing);
      return fieldVersion.get(field) ?? -1;
    },
  };
}
